/* What the trading produced, as against what the book is worth.
 *
 * These two get confused constantly and they are not the same question. The
 * value chart answers "what is it worth", and its answer moves every day
 * whether or not anything was done. This screen answers "what did the trading
 * itself produce", and nothing on it moves because a price moved — a position
 * up forty percent contributes here only when some of it is sold.
 *
 * It sits behind the value chart rather than beside the quarterly results,
 * where it was: earnings are what a company did, and these are what you did.
 * Putting them on one screen made both harder to read. */

import { useState } from 'react';
import { TabBar } from '../components/TabBar';
import { HideToggle } from '../components/HideToggle';
import { money, moneySigned, pct, pctSigned, plural, shortDate } from '../lib/format';
import { realisedByDay } from '../state/realised';
import { standings, type Standing } from '../state/ranking';
import { useDisplay } from '../state/display';
import { useStore } from '../state/store';
import type { Nav } from '../App';

type Window = '1M' | '3M' | 'YTD' | 'All';

const WINDOWS: Window[] = ['1M', '3M', 'YTD', 'All'];

function windowStart(w: Window, now = new Date()): string | undefined {
  const day = 86_400_000;
  switch (w) {
    case '1M': return new Date(now.getTime() - 30 * day).toISOString().slice(0, 10);
    case '3M': return new Date(now.getTime() - 90 * day).toISOString().slice(0, 10);
    case 'YTD': return `${now.getUTCFullYear()}-01-01`;
    case 'All': return undefined;
  }
}

export function Results({ nav }: { nav: Nav }) {
  const { state, book } = useStore();
  const { currency, toDisplay } = useDisplay();
  const [window, setWindow] = useState<Window>('All');

  const stats = standings(book.positions);
  const realised = realisedByDay({
    trades: state.data.trades,
    perps: state.data.perps,
    accounts: state.data.accounts,
    fx: state.fx,
    ...(windowStart(window) ? { from: windowStart(window)! } : {}),
  });

  const worstBar = Math.max(
    1,
    ...realised.days.map((d) => Math.abs(d.usd)),
  );

  return (
    <div className="screen" data-tabbed="true">
      <div className="screen-head">
        <button className="back" onClick={nav.back} aria-label="Back">‹</button>
        <h1>Results</h1>
        <span className="meta" style={{ marginLeft: 'auto' }}>realised only</span>
        <HideToggle />
      </div>

      <div className="card hero">
        <div className="hero-label">Taken off the table</div>
        <div className={`hero-total num ${realised.totalUsd >= 0 ? 'up' : 'down'}`}>
          {moneySigned(toDisplay(realised.totalUsd), currency)}
        </div>
        <div className="hero-change">
          <span className="dim" style={{ fontSize: 12 }}>
            {realised.days.length === 0
              ? 'nothing realised in this window'
              : `${plural(realised.days.length, 'day')} · ${realised.greenDays} green · ${realised.redDays} red`}
          </span>
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>
          Sales and closed contracts only. Nothing here moves because a price moved. A holding up
          forty percent counts for nothing until some of it is sold, which is what makes this a
          different figure from the one on the value chart.
        </div>

        <div className="seg" style={{ marginTop: 12 }}>
          {WINDOWS.map((w) => (
            <button key={w} aria-pressed={window === w} onClick={() => setWindow(w)}>{w}</button>
          ))}
        </div>
      </div>

      {realised.days.length > 0 ? (
        <section>
          <div className="section-head">
            <h2>Day by day</h2>
            <span className="meta">
              {money(toDisplay(realised.fromSharesUsd), currency)} shares ·{' '}
              {money(toDisplay(realised.fromContractsUsd), currency)} contracts
            </span>
          </div>
          <div className="card card-pad">
            {[...realised.days].reverse().map((d, i, all) => (
              <div key={d.date} style={{ marginBottom: i === all.length - 1 ? 0 : 10 }}>
                <div className="kv">
                  <span className="k num" style={{ fontSize: 12 }}>{shortDate(d.date)}</span>
                  {d.events > 1 ? (
                    <span className="dim" style={{ fontSize: 11 }}>{d.events} closes</span>
                  ) : null}
                  <span className={`v num ${d.usd >= 0 ? 'up' : 'down'}`} style={{ marginLeft: 'auto' }}>
                    {moneySigned(toDisplay(d.usd), currency)}
                  </span>
                </div>
                {/* The bar is scaled to the biggest day either way, so a loss
                    and a gain of the same size read the same length. */}
                <div style={{
                  height: 3, marginTop: 4, borderRadius: 2,
                  width: `${Math.max(2, (Math.abs(d.usd) / worstBar) * 100)}%`,
                  background: d.usd >= 0 ? 'var(--up)' : 'var(--down)', opacity: 0.75,
                }} />
              </div>
            ))}
          </div>

          {realised.bestDay && realised.worstDay && realised.days.length > 1 ? (
            <div className="card card-pad" style={{ marginTop: 8 }}>
              <div className="kv">
                <span className="k">Best day</span>
                <span className="num" style={{ marginLeft: 8 }}>{shortDate(realised.bestDay.date)}</span>
                <span className="v num up" style={{ marginLeft: 'auto' }}>
                  {moneySigned(toDisplay(realised.bestDay.usd), currency)}
                </span>
              </div>
              <div className="kv">
                <span className="k">Worst day</span>
                <span className="num" style={{ marginLeft: 8 }}>{shortDate(realised.worstDay.date)}</span>
                <span className="v num down" style={{ marginLeft: 'auto' }}>
                  {moneySigned(toDisplay(realised.worstDay.usd), currency)}
                </span>
              </div>
            </div>
          ) : null}

          {realised.salesWithoutGain > 0 || realised.skippedForRate > 0 ? (
            <div className="warn" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>
              {realised.salesWithoutGain > 0
                ? `${plural(realised.salesWithoutGain, 'sale')} carry no recorded gain and are not in these totals, counted apart rather than as breaking even. `
                : ''}
              {realised.skippedForRate > 0
                ? `${plural(realised.skippedForRate, 'item')} left out: no rate known for the currency, and nothing is converted at another one.`
                : ''}
            </div>
          ) : null}
        </section>
      ) : (
        <section>
          <div className="card card-pad">
            <p style={{ fontSize: 13.5, lineHeight: 1.55 }}>Nothing realised in this window.</p>
            <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 8 }}>
              A sale reaches this through the trade log, and a contract through being closed. Until
              something is taken off the table there is nothing here to count, which is different
              from having made nothing, and the value chart is where that question is answered.
            </p>
          </div>
        </section>
      )}

      {/* Moved here from the earnings screen. What a company reported and what
          your position did are two subjects, and one screen was carrying both. */}
      {stats.byReturn.length > 0 ? (
        <section>
          <div className="section-head">
            <h2>Best and worst</h2>
            <span className="meta">{stats.upCount} up · {stats.downCount} down</span>
          </div>
          <div className="card card-pad">
            <div className="kv">
              <span className="k">Standing gain, on what has a cost</span>
              <span className={`v num ${stats.totalGainUsd >= 0 ? 'up' : 'down'}`}>
                {moneySigned(toDisplay(stats.totalGainUsd), currency)}
              </span>
            </div>
            <div className="divider" />
            <StatRow label="Best return" s={stats.best} kind="pct" toDisplay={toDisplay} currency={currency} />
            <StatRow label="Worst return" s={stats.worst} kind="pct" toDisplay={toDisplay} currency={currency} />
            <div className="divider" />
            <StatRow label="Made the most" s={stats.biggestEarner} kind="money" toDisplay={toDisplay} currency={currency} />
            <StatRow label="Cost the most" s={stats.biggestDrag} kind="money" toDisplay={toDisplay} currency={currency} />
            <div className="dim" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.45 }}>
              These are unrealised: what the positions stand at now, not what was taken. The top two
              are the calls that went furthest, the bottom two are where the money went, rarely the
              same name, because a holding up 300% on half a percent of the book is a better call
              and a smaller earner than one up 20% on a third of it.
            </div>
          </div>
        </section>
      ) : null}

      {stats.byReturn.length > 0 ? (
        <section>
          <div className="section-head">
            <h2>Every position</h2>
            <span className="meta">from what you paid</span>
          </div>
          <div className="card card-pad">
            {stats.byReturn.map((p, i) => (
              <div className="kv" key={p.ticker} style={{ marginBottom: i === stats.byReturn.length - 1 ? 0 : 9 }}>
                <span className="dim num" style={{ fontSize: 11, minWidth: 16 }}>{i + 1}</span>
                <span style={{ fontWeight: 600, color: 'var(--text)' }}>{p.ticker}</span>
                <span className="dim num" style={{ fontSize: 11, marginLeft: 8 }}>{pct(p.weight)}</span>
                <span className="v num" style={{ marginLeft: 'auto' }}>
                  {moneySigned(toDisplay(p.gainUsd), currency)}
                </span>
                <span className={`num ${p.returnPct >= 0 ? 'up' : 'down'}`}
                      style={{ minWidth: 68, textAlign: 'right' }}>
                  {p.returnPct >= 0 ? '▲' : '▼'} {pctSigned(p.returnPct)}
                </span>
              </div>
            ))}
          </div>
          {stats.withoutCost > 0 ? (
            <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
              {plural(stats.withoutCost, 'position')} left out: no average cost on record, so there
              is nothing to measure a return against.
            </div>
          ) : null}
        </section>
      ) : null}

      <TabBar current="value" go={nav.go} />
    </div>
  );
}

function StatRow({ label, s, kind, toDisplay, currency }: {
  label: string;
  s: Standing | null;
  kind: 'pct' | 'money';
  toDisplay: (usd: number) => number;
  currency: 'USD' | 'PLN';
}) {
  if (!s) return null;
  const figure = kind === 'pct' ? s.returnPct : s.gainUsd;
  return (
    <div className="kv" style={{ marginBottom: 6 }}>
      <span className="k">{label}</span>
      <span style={{ fontWeight: 600, color: 'var(--text)', marginLeft: 8 }}>{s.ticker}</span>
      <span className={`v num ${figure >= 0 ? 'up' : 'down'}`} style={{ marginLeft: 'auto' }}>
        {kind === 'pct' ? pctSigned(s.returnPct) : moneySigned(toDisplay(s.gainUsd), currency)}
      </span>
    </div>
  );
}
